import { useEffect, type FunctionComponent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, DatePicker, Form, Input, InputNumber, Modal, Radio, Select, Switch, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { mediaLibraryActions } from "@fitness-app/app-store";
import { type ChallengeEventEntity } from "@fitness-app/data-models/entities/Challenge";
import { challengeFeedEnabled } from "@fitness-app/data-models/entities/ChallengeFeed";
import { MediaLibraryResourceType } from "@fitness-app/data-models/entities/MediaLibrary";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { createMediaLibraryVideoOptions } from "~/modules/TrainingPrograms/tabs/ExercisesDatabase/ExerciseForm/types";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { challengeEventDayOfWeekOptions } from "./constants";
import { generateWeeklyOccurrences } from "./helpers";

type Props = {
  open: boolean;
  challengeId: string;
  /** Challenge window — the default range of a generated series. */
  challengeStartDate: string | null | undefined;
  challengeEndDate: string | null | undefined;
  /** none = the challenge has no feed, so an event cannot host a discussion thread. */
  challengeFeedMode: string | null | undefined;
  event: ChallengeEventEntity | null;
  /** Slot picked on the calendar view — wins over the challenge-window default when creating. */
  defaultStart?: Dayjs | null;
  defaultDurationMinutes?: number | null;
  /** "weekly" when the trainer picked "dodaj serię" from the calendar's context menu. */
  defaultRepeat?: "once" | "weekly";
  onClose: () => void;
};

type EventFormValues = {
  title: string;
  description?: string;
  startAt: Dayjs;
  durationMinutes?: number;
  linkUrl?: string;
  linkLabel?: string;
  mediaLibraryId?: string | null;
  /** Creates (or keeps) the challenge_post that hosts this event's comments. */
  withDiscussion?: boolean;
  repeat: "once" | "weekly";
  daysOfWeek?: number[];
  repeatUntil?: Dayjs;
};

const toFormValues = (event: ChallengeEventEntity): EventFormValues => {
  const startAt = dayjs(event.startAt);
  return {
    title: event.title,
    description: event.description ?? undefined,
    startAt,
    durationMinutes: event.endAt ? dayjs(event.endAt).diff(startAt, "minute") : undefined,
    linkUrl: event.linkUrl ?? undefined,
    linkLabel: event.linkLabel ?? undefined,
    mediaLibraryId: event.mediaLibraryId ?? undefined,
    withDiscussion: Boolean(event.postId),
    repeat: "once",
  };
};

const ChallengeEventFormModal: FunctionComponent<Props> = ({
  open,
  challengeId,
  challengeStartDate,
  challengeEndDate,
  challengeFeedMode,
  event,
  defaultStart,
  defaultDurationMinutes,
  defaultRepeat,
  onClose,
}) => {
  const [form] = Form.useForm<EventFormValues>();
  const queryClient = useQueryClient();
  const { memberId } = useUserClaims();
  const userId = useAppSelector((store) => store.user.data?.id);
  const isEdit = Boolean(event);
  const repeat = Form.useWatch("repeat", form);
  const dispatch = useAppDispatch();
  const { list: mediaLibrary, listStatus } = useAppSelector((store) => store.mediaLibrary);
  const feedAvailable = challengeFeedEnabled(challengeFeedMode);

  useEffect(() => {
    if (open && !listStatus) {
      dispatch(mediaLibraryActions.subscribeToMediaLibrary(MediaLibraryResourceType.Challenge));
    }
  }, [open, listStatus, dispatch]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (event) {
      form.setFieldsValue(toFormValues(event));
      return;
    }
    // A new event defaults to the challenge start (or today, for a challenge already under way) at
    // 10:00 — unless it was started from a slot on the calendar, which already carries a date.
    const windowStart = challengeStartDate ? dayjs(challengeStartDate) : dayjs();
    const firstDay = windowStart.isBefore(dayjs(), "day") ? dayjs() : windowStart;
    form.setFieldsValue({
      title: "",
      description: undefined,
      startAt: defaultStart ?? firstDay.hour(10).minute(0).second(0).millisecond(0),
      durationMinutes: defaultDurationMinutes ?? 60,
      linkUrl: undefined,
      linkLabel: undefined,
      mediaLibraryId: undefined,
      withDiscussion: false,
      repeat: defaultRepeat ?? "once",
      // Empty = the weekday of the first occurrence, which is exactly what "co wtorek" means here.
      daysOfWeek: [],
      repeatUntil: challengeEndDate ? dayjs(challengeEndDate) : undefined,
    });
  }, [open, event, challengeStartDate, challengeEndDate, defaultStart, defaultDurationMinutes, defaultRepeat, form]);

  const saveMutation = useMutation({
    mutationFn: async (values: EventFormValues) => {
      const base = {
        challengeId,
        title: values.title.trim(),
        description: values.description?.trim() || null,
        linkUrl: values.linkUrl?.trim() || null,
        linkLabel: values.linkLabel?.trim() || null,
        mediaLibraryId: values.mediaLibraryId || null,
      };

      /**
       * The discussion thread IS a feed post — created once, then referenced by the event. Turning
       * the switch off only unlinks it: deleting a post would take its comments with it, which is
       * not what "hide the thread" should mean.
       */
      const resolvePostId = async (currentPostId: string | null): Promise<string | null> => {
        // The switch is disabled for a new weekly series but keeps its value, and a range that
        // happens to yield one occurrence has no seriesId — so the rule is checked on `repeat`.
        const seriesBeingCreated = !event && values.repeat === "weekly";
        if (!values.withDiscussion || !feedAvailable || seriesBeingCreated) {
          return null;
        }
        if (currentPostId) {
          return currentPostId;
        }
        if (!userId) {
          throw new Error("Brak zalogowanego użytkownika");
        }
        const { data, error } = await supabase
          .from("challenge_post")
          .insert({
            challengeId,
            authorId: userId,
            title: values.title.trim(),
            content: values.description?.trim() || values.title.trim(),
            media: [] as never,
          } as never)
          .select("id")
          .single();

        if (error) {
          throw new Error(error.message);
        }
        return (data as { id: string }).id;
      };

      const withTimes = (startAt: Dayjs) => ({
        ...base,
        startAt: startAt.toISOString(),
        endAt: values.durationMinutes ? startAt.add(values.durationMinutes, "minute").toISOString() : null,
      });

      if (event) {
        const postId = await resolvePostId(event.postId);
        const { error } = await supabase
          .from("challenge_event")
          .update({ ...withTimes(values.startAt), postId, updatedAt: new Date().toISOString() } as never)
          .eq("id", event.id);

        if (error) {
          throw new Error(error.message);
        }
        return 1;
      }

      // A weekly rule is materialized into one row per occurrence, sharing a seriesId, so reads stay
      // a plain ordered select and a single occurrence can later be moved or deleted on its own.
      const occurrences =
        values.repeat === "weekly" && values.repeatUntil
          ? generateWeeklyOccurrences(values.startAt, values.repeatUntil, values.daysOfWeek ?? [])
          : [values.startAt];

      if (!occurrences.length) {
        throw new Error("Wybrany zakres nie zawiera żadnego terminu");
      }

      const seriesId = occurrences.length > 1 ? crypto.randomUUID() : null;
      // One thread per series would spam the feed with eight identical posts, so a generated series
      // gets none — the trainer turns the discussion on for the single occurrence that needs it.
      const postId = await resolvePostId(null);
      const rows = occurrences.map((startAt) => ({
        ...withTimes(startAt),
        seriesId,
        postId,
        createdBy: memberId ?? null,
      }));

      const { error } = await supabase.from("challenge_event").insert(rows as never);

      if (error) {
        throw new Error(error.message);
      }
      return rows.length;
    },
    onSuccess: (count) => {
      void message.success(
        isEdit ? "Wydarzenie zapisane" : count > 1 ? `Dodano ${count} terminów` : "Wydarzenie dodane",
      );
      void queryClient.invalidateQueries({ queryKey: ["challenge-events", challengeId] });
      void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
      onClose();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się zapisać wydarzenia: ${error.message}`);
    },
  });

  return (
    <Modal
      open={open}
      title={isEdit ? "Edytuj wydarzenie" : "Dodaj wydarzenie"}
      okText="Zapisz"
      cancelText="Anuluj"
      confirmLoading={saveMutation.isLoading}
      onCancel={onClose}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form<EventFormValues>
        form={form}
        layout="vertical"
        preserve={false}
        onFinish={(values) => saveMutation.mutate(values)}
      >
        <Form.Item
          name="title"
          label="Nazwa wydarzenia"
          rules={[{ required: true, whitespace: true, message: "Podaj nazwę wydarzenia" }]}
        >
          <Input placeholder="np. Sobotni trening na żywo" />
        </Form.Item>

        <Form.Item name="description" label="Opis">
          <Input.TextArea rows={2} placeholder="Krótki opis widoczny w aplikacji" />
        </Form.Item>

        <Form.Item
          name="startAt"
          label={repeat === "weekly" ? "Pierwszy termin (data i godzina)" : "Termin (data i godzina)"}
          rules={[{ required: true, message: "Wybierz termin" }]}
        >
          <DatePicker showTime={{ format: "HH:mm" }} format="DD.MM.YYYY HH:mm" style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="durationMinutes"
          label="Czas trwania (min)"
          tooltip="Puste = zakładamy 60 minut przy oznaczaniu „trwa teraz” w aplikacji."
        >
          <InputNumber min={5} max={1440} step={15} style={{ width: "100%" }} placeholder="60" />
        </Form.Item>

        <Form.Item
          name="linkUrl"
          label="Link"
          tooltip="Transmisja, post na Facebooku, spotkanie — cokolwiek klientka ma otworzyć."
          rules={[
            {
              pattern: /^https?:\/\/.+/i,
              message: "Link musi zaczynać się od http:// lub https://",
            },
          ]}
        >
          <Input placeholder="https://..." />
        </Form.Item>

        <Form.Item name="linkLabel" label="Etykieta linku">
          <Input placeholder="np. Dołącz do transmisji" />
        </Form.Item>

        <Form.Item
          name="mediaLibraryId"
          label="Wideo w aplikacji"
          tooltip="Nagranie z biblioteki mediów (typ „challenge”). Klientka obejrzy je bez wychodzenia z aplikacji, od godziny rozpoczęcia wydarzenia."
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Bez wideo"
            options={createMediaLibraryVideoOptions(mediaLibrary)}
          />
        </Form.Item>

        {feedAvailable ? (
          <Form.Item
            name="withDiscussion"
            label="Wątek dyskusji"
            valuePropName="checked"
            tooltip={
              repeat === "weekly"
                ? "Seria nie zakłada wątków — włącz je później przy wybranym terminie."
                : "Tworzy post na tablicy wyzwania; komentarze pod wydarzeniem to komentarze tego posta."
            }
          >
            <Switch disabled={!isEdit && repeat === "weekly"} />
          </Form.Item>
        ) : null}

        {isEdit ? (
          <Alert
            type="info"
            showIcon
            message="Edytujesz pojedynczy termin"
            description="Zmiany nie przenoszą się na pozostałe terminy serii."
          />
        ) : (
          <>
            <Form.Item name="repeat" label="Powtarzalność">
              <Radio.Group
                optionType="button"
                options={[
                  { value: "once", label: "Jednorazowe" },
                  { value: "weekly", label: "Co tydzień" },
                ]}
              />
            </Form.Item>

            {repeat === "weekly" ? (
              <>
                <Form.Item name="daysOfWeek" label="Dni tygodnia" tooltip="Puste = dzień tygodnia pierwszego terminu.">
                  <Select mode="multiple" allowClear options={challengeEventDayOfWeekOptions} placeholder="np. Sob" />
                </Form.Item>
                <Form.Item
                  name="repeatUntil"
                  label="Powtarzaj do"
                  rules={[{ required: true, message: "Podaj datę końca serii" }]}
                >
                  <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
                </Form.Item>
              </>
            ) : null}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ChallengeEventFormModal;
